import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';

/**
 * Service to manage info tooltip state
 * Tracks which tooltips have been shown to the user
 */
@Injectable({
  providedIn: 'root',
})
export class InfoTooltipService {
  private readonly TOOLTIP_PREFIX = 'infoTooltip_';

  constructor(private storageService: StorageService) {}

  /**
   * Check if a tooltip has been seen
   * @param tooltipId Unique identifier for the tooltip (e.g., 'tts_home', 'recovery_intro')
   * @param userId Optional user ID - if provided, tooltip state is user-specific
   */
  async hasSeenTooltip(tooltipId: string, userId?: string | number): Promise<boolean> {
    const key = this.getTooltipKey(tooltipId, userId);
    try {
      const value = await this.storageService.gapSetting(key);
      return value === 'seen';
    } catch (error) {
      console.error('InfoTooltipService: Error checking tooltip state:', error);
      return false;
    }
  }

  /**
   * Mark a tooltip as seen
   * @param tooltipId Unique identifier for the tooltip
   * @param userId Optional user ID - if provided, tooltip state is user-specific
   */
  async markTooltipAsSeen(tooltipId: string, userId?: string | number): Promise<void> {
    const key = this.getTooltipKey(tooltipId, userId);
    try {
      await this.storageService.gapSetting(key, 'seen');
      console.log(`InfoTooltipService: Marked tooltip '${tooltipId}' as seen`);
    } catch (error) {
      console.error('InfoTooltipService: Error marking tooltip as seen:', error);
    }
  }

  /**
   * Reset a tooltip (mark as not seen)
   * @param tooltipId Unique identifier for the tooltip
   * @param userId Optional user ID - if provided, resets tooltip state for that user only
   */
  async resetTooltip(tooltipId: string, userId?: string | number): Promise<void> {
    const key = this.getTooltipKey(tooltipId, userId);
    try {
      await this.storageService.gapSetting(key, 'not_seen');
      console.log(`InfoTooltipService: Reset tooltip '${tooltipId}'`);
    } catch (error) {
      console.error('InfoTooltipService: Error resetting tooltip:', error);
    }
  }

  /**
   * Reset all tooltips
   */
  async resetAllTooltips(): Promise<void> {
    try {
      // Get all gapsettings
      const allSettings = await this.storageService.getAllGapSettings();

      // Find all tooltip settings
      const tooltipSettings = allSettings.filter(setting =>
        setting.key.startsWith(this.TOOLTIP_PREFIX),
      );

      // Reset all tooltip settings in parallel
      await Promise.all(
        tooltipSettings.map(setting =>
          this.storageService.gapSetting(setting.key, 'not_seen'),
        ),
      );
      console.log('InfoTooltipService: Reset all tooltips');
    } catch (error) {
      console.error('InfoTooltipService: Error resetting all tooltips:', error);
    }
  }

  /**
   * Get the storage key for a tooltip
   * description: Converts a tooltip ID into a storage key by adding a prefix, replaces '/' with '_', and removes query parameters.
   * If userId is provided, includes it in the key to make tooltips user-specific.
   */
  private getTooltipKey(tooltipId: string, userId?: string | number): string {
    // Sanitize tooltip ID - replace / with _ and remove query params
    const sanitized = tooltipId.replace(/\//g, '_').split('?')[0];
    
    // If userId is provided, include it in the key for user-specific tooltips
    if (userId !== undefined && userId !== null) {
      return `${this.TOOLTIP_PREFIX}${sanitized}_userId_${userId}`;
    }
    
    return `${this.TOOLTIP_PREFIX}${sanitized}`;
  }

  /**
   * Get all seen tooltips
   */
  async getSeenTooltips(): Promise<string[]> {
    try {
      const allSettings = await this.storageService.getAllGapSettings();
      const seenTooltips: string[] = [];

      for (const setting of allSettings) {
        if (setting.key.startsWith(this.TOOLTIP_PREFIX) && setting.value === 'seen') {
          // Remove prefix to get original tooltip ID
          const tooltipId = setting.key.replace(this.TOOLTIP_PREFIX, '');
          seenTooltips.push(tooltipId);
        }
      }

      return seenTooltips;
    } catch (error) {
      console.error('InfoTooltipService: Error getting seen tooltips:', error);
      return [];
    }
  }
}
